import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, RefreshCw } from "lucide-react";

import { PageHeader, PageBody } from "@/components/PageChrome";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import ImageThumb from "@/components/ImageThumb";
import { api, formatApiError } from "@/lib/api";
import { usePerms } from "@/lib/auth";

/**
 * Picklist module — Mar 3 2026.
 * ---------------------------------------------------------------------
 * Warehouse-floor picking view. Two sections in one page:
 *   • Today's Shipments — flex/easy-ship accounts whose shipping_date is today
 *   • Daily Ship       — every order whose working_status is Daily-Ship
 *
 * Both sections group orders by ``system_sku`` and display an editable
 * ``Qty Picked`` per group. Persistence lives in ``picklist_progress`` so
 * refreshing / order churn does not lose the picker's progress.
 */

function ZoomImage({ src, alt }) {
  return <ImageThumb src={src} alt={alt} size={64} testId="picklist-thumb" />;
}

function PicklistSection({ title, rows, onPickedChange, sectionKey, saving }) {
  return (
    <section className="mb-8" data-testid={`picklist-section-${sectionKey}`}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-heading text-[15px] font-bold text-slate-900">{title}</h2>
        <span className="text-[11.5px] text-slate-500 font-mono">
          {rows.length} {rows.length === 1 ? "SKU" : "SKUs"}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-sm border border-dashed border-slate-200 bg-slate-50/60 py-8 text-center text-[13px] text-slate-500">
          No rows match the current filters.
        </div>
      ) : (
        <div className="overflow-auto rounded-sm border border-slate-200 bg-white">
          <table className="erp-table w-full">
            <thead>
              <tr>
                <th className="w-16">Image</th>
                <th className="w-24">Product URL</th>
                <th>System SKU</th>
                <th className="w-24">Bin No</th>
                <th>Product</th>
                <th className="text-right w-20">Qty</th>
                <th className="text-right w-28">Qty Picked</th>
                <th className="text-right w-24">Pending</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.system_sku}
                  data-testid={`picklist-row-${sectionKey}-${r.system_sku}`}
                  data-done={r.done ? "1" : "0"}
                  className={r.done ? "bg-emerald-100/80" : ""}
                >
                  <td>
                    <ZoomImage src={r.image_url} alt={r.product_name} />
                  </td>
                  <td>
                    {r.product_url ? (
                      <a
                        href={r.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-700 hover:underline text-[11.5px] font-mono"
                        data-testid={`picklist-url-${sectionKey}-${r.system_sku}`}
                      >
                        Open ↗
                      </a>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="font-mono font-semibold">{r.system_sku}</td>
                  <td
                    className="font-mono text-[11.5px]"
                    data-testid={`picklist-bin-${sectionKey}-${r.system_sku}`}
                  >
                    {r.bin_id
                      ? <span className="inline-flex items-center h-5 px-1.5 rounded-sm bg-slate-100 text-slate-800 border border-slate-200">{r.bin_id}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="truncate max-w-[280px] text-[12px] text-slate-700">
                    {r.product_name || "—"}
                  </td>
                  <td className="text-right font-mono">{r.qty}</td>
                  <td className="text-right">
                    <Input
                      type="number"
                      min="0"
                      value={r.qty_picked ?? 0}
                      onChange={(e) => onPickedChange(sectionKey, r.system_sku, e.target.value)}
                      onBlur={(e) => onPickedChange(sectionKey, r.system_sku, e.target.value, true)}
                      disabled={saving[`${sectionKey}::${r.system_sku}`]}
                      testId={`picklist-picked-${sectionKey}-${r.system_sku}`}
                      className={`w-20 text-right font-mono ${r.done ? "border-emerald-400 bg-emerald-50" : ""}`}
                    />
                  </td>
                  <td className="text-right font-mono font-semibold text-slate-800">
                    {r.pending_qty}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function Picklist() {
  const { allowedWarehouses } = usePerms();
  const [warehouses, setWarehouses] = useState([]);
  const [flexAccounts, setFlexAccounts] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [todays, setTodays] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [meta, setMeta] = useState({});

  // Bootstrap: warehouse + flex/easy-ship accounts.
  useEffect(() => {
    api.get("/warehouse")
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const allow = allowedWarehouses();
        const filtered = allow
          ? items.filter((w) => allow.includes(w.name))
          : items;
        setWarehouses(filtered.map((w) => w.name).filter(Boolean));
      })
      .catch(() => setWarehouses([]));
    api.get("/picklist/accounts")
      .then((r) => setFlexAccounts(r.data?.items || []))
      .catch(() => setFlexAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedWarehouses.length) params.warehouse_name = selectedWarehouses.join(",");
      if (selectedAccounts.length) params.account_id = selectedAccounts.join(",");
      const { data } = await api.get("/picklist", { params });
      setTodays(data.todays_shipments || []);
      setDaily(data.daily_ship || []);
      setMeta(data.meta || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouses, selectedAccounts]);

  useEffect(() => { load(); }, [load]);

  // Optimistic local update — the Input reflects the picker's typing
  // immediately; commit=true (onBlur) persists via API and reloads server
  // truth so pending_qty / done state stay in sync.
  const onPickedChange = async (sectionKey, sku, rawValue, commit = false) => {
    const value = rawValue === "" ? 0 : Number(rawValue);
    const safeValue = Number.isNaN(value) ? 0 : Math.max(0, value);
    const setter = sectionKey === "todays_shipments" ? setTodays : setDaily;
    setter((rows) => rows.map((r) => r.system_sku === sku
      ? { ...r, qty_picked: safeValue, pending_qty: Math.max(0, r.qty - safeValue), done: safeValue >= r.qty && r.qty > 0 }
      : r));
    if (!commit) return;
    const key = `${sectionKey}::${sku}`;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const params = {};
      if (selectedWarehouses.length === 1) params.warehouse_name = selectedWarehouses[0];
      await api.patch(`/picklist/${sectionKey}/${encodeURIComponent(sku)}`,
                      { qty_picked: safeValue }, { params });
    } catch (e) {
      toast.error(formatApiError(e));
      // Restore on failure by reloading the server snapshot.
      load();
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const accountOptions = useMemo(
    () => flexAccounts.map((a) => a.account_id).filter(Boolean),
    [flexAccounts],
  );

  return (
    <>
      <PageHeader
        title="Picklist"
        subtitle={meta.today ? `Today: ${meta.today}` : "Warehouse floor picking"}
        testId="picklist-title"
      >
        <Button onClick={load} disabled={loading} testId="picklist-refresh">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </Button>
      </PageHeader>
      <PageBody>
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Warehouse</label>
            <MultiSelectFilter
              label="warehouses"
              options={warehouses}
              value={selectedWarehouses}
              onChange={setSelectedWarehouses}
              testId="picklist-warehouse-filter"
              className="min-w-[220px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              Account <span className="text-slate-400 font-normal normal-case">(Easy-Ship / Seller-Flex)</span>
            </label>
            <MultiSelectFilter
              label="accounts"
              options={accountOptions}
              value={selectedAccounts}
              onChange={setSelectedAccounts}
              testId="picklist-account-filter"
              className="min-w-[260px]"
            />
          </div>
          <div className="ml-auto text-[11px] text-slate-500 flex items-center gap-1">
            <ClipboardList size={13} />
            <span>Ticked rows turn green when Pending reaches 0.</span>
          </div>
        </div>

        <PicklistSection
          title="Section 1 — Today's Shipments"
          rows={todays}
          onPickedChange={onPickedChange}
          sectionKey="todays_shipments"
          saving={saving}
        />
        <PicklistSection
          title="Section 2 — Daily Ship"
          rows={daily}
          onPickedChange={onPickedChange}
          sectionKey="daily_ship"
          saving={saving}
        />
      </PageBody>
    </>
  );
}
